/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Teachr from '../Objects/models/Teachr';
import TeachrContactRepository from '../Repositories/TeachrContactRepository';
import ApiService from '../Services/ApiService';
import validateEmail from '../Validation/Email';
import { validatePhoneNumber } from '../Validation/Phone';
import validateZipCode from '../Validation/zipCode';
import { computeAddressErrors as computeManualAddressErrors } from '../Utils/manualAddress';
import validateDateAici from '../Validation/DateAici';
import ValidateIbanAici from '../Validation/IbanAici';
import ValidateBIC from '../Validation/Bic';
import {
    SET_AICI_ONBOARDING_INFO,
} from '../Redux/Reducers/AiciOnboardingReducers';
import { SHOW_ERROR } from '../Redux/Reducers/MessageReducers';
import { runAiciSubmission, runAiciSelfSubmission, AiciSubmissionError } from '../App/AiciOnboarding/runAiciSubmission';
import ClientRepository from '../Repositories/ClientRepository';
import AddressRepository from '../Repositories/AddressRepository';
import ChildRepository from '../Repositories/ChildRepository';
import { userClientSetAccountAction } from '../Redux/Actions/UserActions';
import { AiciOnboardingContextValue } from '../Contexts/AiciOnboardingContexte';
import {
    AddressData,
    AddressErrors,
    AiciStep,
    BirthBankData,
    BirthBankErrors,
    ConditionsData,
    ConditionsErrors,
    PersonalInfoData,
    PersonalInfoErrors,
} from './InterfaceAiciOnboarding';

const FRANCE = 'FRANCE';

/**
 * Nom / prénom règles URSSAF/DGFIP (API R2P de reconnaissance des personnes physiques) :
 * lettres NON accentuées uniquement, plus l'apostrophe ('), le tiret (-) et l'espace ( ) ;
 * ne peut ni commencer ni finir par un caractère spécial ; 80 caractères maximum.
 * Les ACCENTS sont interdits (le nom sert à la reconnaissance du particulier auprès de la DGFIP).
 * Pattern de référence URSSAF : ^[A-Za-z]+(([A-Za-z'\- ])*)+([A-Za-z])|(^[A-Za-z])$
 */
const NAME_PATTERN = /^[A-Za-z]([A-Za-z'\- ]*[A-Za-z])?$/;
const NAME_MAX = 80;
const NAME_MSG = "Le nom ne doit comporter ni chiffres, ni accents, ni caractères spéciaux (hormis l'apostrophe, le tiret et l'espace) et ne peut ni commencer ni finir par l'un d'eux.";

/** Valide un champ nom/prénom selon les règles URSSAF. Renvoie le message d'erreur ou undefined. */
const validateNameField = (value: string, required: boolean): string | undefined => {
    const v = value.trim();
    if (!v) return required ? 'Ce champ est obligatoire' : undefined;
    if (v.length > NAME_MAX) return `Ce champ ne doit pas dépasser ${NAME_MAX} caractères`;
    if (!NAME_PATTERN.test(v)) return NAME_MSG;
    return undefined;
};

/**
 * Normalise une date de naissance vers le format interne DD-MM-YYYY.
 * Le serveur renvoie la date en ISO 8601 ('2000-06-22T00:00:00+00:00') alors que la saisie
 * front produit 'DD-MM-YYYY'. On unifie ici pour que tous les consommateurs (FormDate,
 * validation, payload URSSAF) travaillent sur un format cohérent.
 */
const toDDMMYYYY = (raw?: string): string => {
    if (!raw) return '';
    if (moment(raw, 'DD-MM-YYYY', true).isValid()) return raw;
    const parsed = moment(raw); // ISO 8601 et autres formats serveur
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : '';
};

/**
 * Met à jour les erreurs EN LIVE pour les seuls champs modifiés (clés du patch) :
 * efface l'erreur si le champ est redevenu valide, la (ré)affiche sinon.
 * Les champs non touchés conservent leur état d'erreur courant.
 */
const mergeLiveErrors = (prev: any, patch: object, computed: any): any => {
    const updated: any = { ...prev };
    Object.keys(patch).forEach((k) => {
        if (computed[k]) updated[k] = computed[k];
        else delete updated[k];
    });
    return updated;
};

const emptyPersonal: PersonalInfoData = {
    gender: '', firstName: '', name: '', birthName: '', mail: '', phoneNumber: '', studentName: '',
};
const emptyBirthBank: BirthBankData = {
    birthDay: '', country: '', birthZipCode: '', birthCity: '', iban: '', bic: '', accountHolder: '',
};
const emptyAddress: AddressData = {
    streetNumber: '', streetLetter: null, streetType: null, streetLabel: '',
    complement: '', lieuDit: '', zipCode: '', city: '',
};

/** Parse le champ JSON addressObject du TeachrContact (objet ou chaîne) en AddressData partiel. */
const parseAddressObject = (raw: any): Partial<AddressData> | null => {
    if (!raw) return null;
    if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return null; }
    }
    return typeof raw === 'object' ? raw : null;
};
const emptyConditions: ConditionsData = {
    terms: false, cgu: false, cgv: false, isNotify: false, acceptMarketingEmail: false,
};

/**
 * Hook central du parcours d'inscription AICI.
 * Appelé UNE fois dans l'orchestrateur : effectue un unique fetch du TeachrContact,
 * détient l'état du formulaire, la validation par étape, la persistance (serveur + Redux)
 * et l'orchestration de soumission. Renvoie la valeur du contexte partagé entre étapes.
 */
export interface AiciSelfOptions {
    /** Activation depuis l'espace client : client connecté, pas de reprise contact. */
    self: true;
    /** Le client connecté (source du préremplissage). */
    client: any;
}

export const useAiciOnboarding = (key?: string, selfOptions?: AiciSelfOptions): AiciOnboardingContextValue => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSelf = !!selfOptions?.self;
    const selfClient = selfOptions?.client;
    // Activation autonome : code d'invitation du prof, optionnel.
    const [invitationCode, setInvitationCode] = useState('');

    // Contact serveur (canonique). Typé loosement : fetchTeachrContactByKey renvoie du JSON brut.
    const [contact, setContact] = useState<any>();
    const [teachr, setTeachr] = useState<Teachr>();
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Reprise instantanée au refresh : étape issue de l'instantané Redux persisté (si même clé).
    const persistedStep = useSelector((state: any) =>
        state?.aiciOnboarding && state.aiciOnboarding.key === key ? state.aiciOnboarding.step : undefined
    );
    const [currentStep, setCurrentStep] = useState<number>(persistedStep ?? AiciStep.PersonalInfo);

    // État du formulaire
    const [personal, setPersonalState] = useState<PersonalInfoData>(emptyPersonal);
    const [birthBank, setBirthBankState] = useState<BirthBankData>(emptyBirthBank);
    const [address, setAddressState] = useState<AddressData>(emptyAddress);
    const [conditions, setConditionsState] = useState<ConditionsData>(emptyConditions);
    // Le titulaire du compte suit prénom + nom tant que l'utilisateur ne l'a pas édité lui-même.
    const [accountHolderTouched, setAccountHolderTouched] = useState(false);

    const [personalErrors, setPersonalErrors] = useState<PersonalInfoErrors>({});
    const [birthBankErrors, setBirthBankErrors] = useState<BirthBankErrors>({});
    const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
    const [conditionsErrors, setConditionsErrors] = useState<ConditionsErrors>({});

    const isBornFrance = (birthBank.country || '').trim().toUpperCase() === FRANCE;

    const store = useSelector((state: any) => state)

    // ── Chargement initial (un seul fetch) ──────────────────────────────────
    useEffect(() => {
        // Mode self (espace client) : préremplissage depuis le client connecté ;
        // une clé de contact éventuelle (invitation du prof) apporte le prof,
        // sans jamais bloquer le parcours si elle est invalide.
        if (isSelf) {
            seedFromClient(selfClient);
            if (key) {
                TeachrContactRepository.fetchTeachrContactByKey(key)
                    .then((response: any) => {
                        if (response?.teachr) setTeachr(response.teachr);
                        if (response) setContact(response);
                    })
                    .catch(() => {
                        console.warn('AICI: clé de contact invalide, activation autonome');
                    })
                    .finally(() => setLoading(false));
            } else {
                setLoading(false);
            }
            return;
        }
        if (!key) {
            setLoading(false);
            setNotFound(true);
            navigate('/notfound');
            return;
        }
        dispatch({ type: 'SHOW_DASHBOARD_AICI_LOADER' });
        TeachrContactRepository.fetchTeachrContactByKey(key)
            .then((response: any) => {
                if (!response) {
                    setNotFound(true);
                    return;
                }
                seedFromContact(response);
            })
            .catch(() => {
                ApiService.disableAuth();
                setNotFound(true);
                navigate('/notfound');
            })
            .finally(() => {
                setLoading(false);
                dispatch({ type: 'HIDE_LOADER' });
            });
    }, [key]);

    /** Mode self : initialise le formulaire depuis le client connecté. */
    const seedFromClient = (client: any) => {
        if (!client) return;
        setPersonalState({
            gender: client.sex === 'MAN' ? '1' : client.sex === 'WOMAN' ? '2' : '',
            firstName: client.firstName ?? '',
            name: client.lastName ?? '',
            birthName: '',
            mail: client.mail ?? '',
            phoneNumber: client.phone ?? '',
            studentName: '',
        });
        setBirthBankState((prev) => ({
            ...prev,
            birthDay: toDDMMYYYY(client.birthday),
            accountHolder: `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim(),
        }));
        setConditionsState((prev) => ({
            ...prev,
            isNotify: client.isNotify ?? true,
            acceptMarketingEmail: client.acceptMarketing ?? false,
        }));
        // Élève existant : préremplir son prénom pour que le backend réutilise
        // sa fiche (sans cela, un élève nommé comme le titulaire serait créé et
        // le prof verrait le client en double dans sa liste d'élèves).
        if (client.id) {
            ChildRepository.getChildren(client.id)
                .then((children: any) => {
                    const list = Array.isArray(children) ? children : (children?.['hydra:member'] ?? []);
                    const first = list.find((c: any) => !c?.isDeleted);
                    if (first?.firstName) {
                        setPersonalState((prev) => (
                            prev.studentName ? prev : { ...prev, studentName: first.firstName }
                        ));
                    }
                })
                .catch(() => { /* préremplissage seulement */ });
        }
        // Adresse existante : préremplissage best effort (numéro, libellé, CP,
        // ville) ; le format URSSAF (type de voie...) reste à compléter.
        const firstAddress = client.addresses?.[0];
        if (firstAddress?.id) {
            AddressRepository.fetchAddressById(firstAddress.id)
                .then((full: any) => {
                    if (!full) return;
                    setAddressState((prev) => ({
                        ...prev,
                        streetNumber: (full.streetNumber ?? '').replace(/\D/g, '').slice(0, 4),
                        streetLabel: (full.streetNameShort || full.streetName || '').trim().slice(0, 28),
                        zipCode: full.zip ?? '',
                        city: full.city ?? '',
                    }));
                })
                .catch(() => { /* préremplissage seulement */ });
        }
    };

    /** Initialise l'état du formulaire à partir du contact serveur. */
    const seedFromContact = (response: any) => {
        setContact(response);
        if (response.teachr) setTeachr(response.teachr);

        setPersonalState({
            gender: response.gender ?? '',
            firstName: response.firstName ?? '',
            name: response.name ?? '',
            birthName: response.birthName ?? '',
            mail: response.mail ?? '',
            phoneNumber: response.phoneNumber ?? '',
            studentName: response.studentName ?? '',
        });

        const country = response.isBornFrance ? 'France' : (response.country ?? '');

        setBirthBankState({
            birthDay: toDDMMYYYY(response.birthDay),
            country,
            birthZipCode: response.zipCode ?? '',
            birthCity: response.city ?? '',
            iban: response.iban ?? '',
            bic: response.bic ?? '',
            // Titulaire du compte : non stocké côté serveur → pré-rempli avec le nom complet.
            accountHolder: `${response.firstName ?? ''} ${response.name ?? ''}`.trim(),
        });

        // Adresse du domicile : restaurée depuis le champ JSON addressObject du TeachrContact
        // (écrit à chaque étape) → survit au refresh et à un autre navigateur.
        const savedAddress = parseAddressObject(response.addressObject);
        if (savedAddress) {
            setAddressState((prev) => ({ ...prev, ...savedAddress }));
        }

        setConditionsState((prev) => ({
            ...prev,
            isNotify: !!response.isNotify,
            acceptMarketingEmail: !!response.acceptMarketingEmail,
        }));

        // Étape initiale : si l'inscription est déjà finalisée (step >= 5 ou status), on va à la
        // confirmation ; sinon on reprend juste après la dernière étape sauvegardée (max = récap).
        const serverStep = Number(response.step) || 0;
        let resumeStep: number;
        if (serverStep >= AiciStep.Confirmation || response.status) {
            resumeStep = AiciStep.Confirmation;
        } else {
            resumeStep = Math.min(serverStep + 1, AiciStep.Recap);
        }
        setCurrentStep(Math.min(Math.max(resumeStep, AiciStep.PersonalInfo), AiciStep.Confirmation));
    };

    // ── Setters (merge partiel) ─────────────────────────────────────────────
    // ── Validation par étape (fonctions pures, sans effet de bord) ───────────
    const computePersonalErrors = (data: PersonalInfoData): PersonalInfoErrors => {
        const errs: PersonalInfoErrors = {};
        if (data.gender !== '1' && data.gender !== '2') errs.gender = 'Veuillez sélectionner une civilité';
        const firstNameErr = validateNameField(data.firstName, true);
        if (firstNameErr) errs.firstName = firstNameErr;
        const nameErr = validateNameField(data.name, true);
        if (nameErr) errs.name = nameErr;
        // Nom de naissance obligatoire pour les femmes (gender '2'), masqué/optionnel sinon.
        const birthNameErr = validateNameField(data.birthName, data.gender === '2');
        if (birthNameErr) errs.birthName = birthNameErr;
        if (!validateEmail(data.mail).isValid) errs.mail = validateEmail(data.mail).message;
        const phoneMsg = validatePhoneNumber(data.phoneNumber);
        if (phoneMsg !== '') errs.phoneNumber = phoneMsg;
        const studentNameErr = validateNameField(data.studentName, false);
        if (studentNameErr) errs.studentName = studentNameErr;
        return errs;
    };

    const computeBirthBankErrors = (data: BirthBankData): BirthBankErrors => {
        const errs: BirthBankErrors = {};
        const bornFrance = (data.country || '').trim().toUpperCase() === FRANCE;
        if (!validateDateAici(data.birthDay).isValid) errs.birthDay = validateDateAici(data.birthDay).message;
        if (!data.country.trim()) errs.country = 'Veuillez sélectionner un pays de naissance';
        if (bornFrance) {
            if (!validateZipCode(data.birthZipCode).isValid) errs.birthZipCode = validateZipCode(data.birthZipCode).message;
            if (!data.birthCity.trim()) errs.birthCity = 'Veuillez sélectionner une ville de naissance';
        }
        if (!ValidateIbanAici(data.iban).isValid) errs.iban = ValidateIbanAici(data.iban).message;
        if (!ValidateBIC(data.bic).isValid) errs.bic = ValidateBIC(data.bic).message;
        if (!data.accountHolder.trim()) errs.accountHolder = 'Veuillez indiquer le titulaire du compte';
        return errs;
    };

    // Règles d'adresse partagées avec le profil client (numéro obligatoire) : src/Utils/manualAddress.ts
    const computeAddressErrors = computeManualAddressErrors;

    const computeConditionsErrors = (data: ConditionsData): ConditionsErrors => {
        const errs: ConditionsErrors = {};
        if (!data.terms) errs.terms = 'Veuillez accepter la politique des données';
        if (!data.cgv) errs.cgv = 'Veuillez accepter les CGU et CGV';
        if (!data.cgu) errs.cgu = "Veuillez accepter la transmission de vos informations à l'URSSAF";
        return errs;
    };

    // Wrappers : valident l'étape entière (au clic Suivant / à la soumission).
    const validatePersonal = (): boolean => {
        const errs = computePersonalErrors(personal);
        setPersonalErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const validateBirthBank = (): boolean => {
        const errs = computeBirthBankErrors(birthBank);
        setBirthBankErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const validateAddress = (): boolean => {
        const errs = computeAddressErrors(address);
        setAddressErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const validateConditions = (): boolean => {
        const errs = computeConditionsErrors(conditions);
        setConditionsErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Setters (merge partiel + validation LIVE des seuls champs modifiés) ───
    const setPersonal = (patch: Partial<PersonalInfoData>) => {
        const next = { ...personal, ...patch };
        setPersonalState(next);
        setPersonalErrors((prev) => mergeLiveErrors(prev, patch, computePersonalErrors(next)));
    };
    const setBirthBank = (patch: Partial<BirthBankData>) => {
        if ('accountHolder' in patch) setAccountHolderTouched(true);
        const next = { ...birthBank, ...patch };
        setBirthBankState(next);
        setBirthBankErrors((prev) => mergeLiveErrors(prev, patch, computeBirthBankErrors(next)));
    };
    const setAddress = (patch: Partial<AddressData>) => {
        const next = { ...address, ...patch };
        setAddressState(next);
        setAddressErrors((prev) => mergeLiveErrors(prev, patch, computeAddressErrors(next)));
    };
    const setConditions = (patch: Partial<ConditionsData>) => {
        const next = { ...conditions, ...patch };
        setConditionsState(next);
        setConditionsErrors((prev) => mergeLiveErrors(prev, patch, computeConditionsErrors(next)));
    };

    // Le titulaire du compte suit prénom + nom (live) tant qu'il n'a pas été modifié manuellement.
    useEffect(() => {
        if (accountHolderTouched) return;
        const full = `${personal.firstName} ${personal.name}`.trim();
        setBirthBankState((prev) => (prev.accountHolder === full ? prev : { ...prev, accountHolder: full }));
    }, [personal.firstName, personal.name, accountHolderTouched]);

    // ── Navigation + persistance ────────────────────────────────────────────
    const persistStep = (step: number) => {
        setCurrentStep(step);
        dispatch({ type: SET_AICI_ONBOARDING_INFO, payload: { step, key: key ?? null } });
    };

    const goToStep = (step: number) => {
        persistStep(step);
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** Reporte les champs du formulaire dans le contact serveur (IRIs normalisées). */
    const applyFormToContact = (target: any) => {
        target.gender = personal.gender;
        target.firstName = personal.firstName.trim();
        target.name = personal.name.trim();
        target.birthName = personal.birthName.trim();
        target.mail = personal.mail;
        target.phoneNumber = personal.phoneNumber;
        target.studentName = personal.studentName.trim();
        // birthDay : le backend rejette une chaîne vide pour ce champ date. On ne l'envoie que
        // s'il est au format DD-MM-YYYY valide ; sinon on l'omet (ne pas écraser la valeur serveur).
        if (birthBank.birthDay && moment(birthBank.birthDay, 'DD-MM-YYYY', true).isValid()) {
            target.birthDay = birthBank.birthDay;
        } else {
            delete target.birthDay;
        }
        target.isBornFrance = isBornFrance;
        target.country = isBornFrance ? '' : birthBank.country;
        target.zipCode = isBornFrance ? birthBank.birthZipCode : '';
        target.city = isBornFrance ? birthBank.birthCity : '';
        target.iban = birthBank.iban;
        target.bic = birthBank.bic;
        target.isNotify = conditions.isNotify;
        target.acceptMarketingEmail = conditions.acceptMarketingEmail;
        // Adresse du domicile (saisie manuelle) persistée en JSON sur le TeachrContact, pour
        // survivre au refresh / autre navigateur. On n'écrit que si renseignée, afin de ne pas
        // écraser une adresse déjà sauvegardée par du vide lors des sauvegardes d'étapes amont.
        if (address.streetLabel || address.zipCode || address.streetNumber) {
            target.addressObject = address;
        }
        // aiciProcess est géré côté serveur (statut de reprise) : ne jamais le ré-émettre.
        delete target.aiciProcess;
        // Normalise les relations en IRIs (évite de sérialiser des objets imbriqués).
        target.teachr = teachr && teachr['@id'] ? teachr['@id'] : (typeof target.teachr === 'string' ? target.teachr : null);
        if (target.address && typeof target.address === 'object') {
            target.address = target.address['@id'] ?? null;
        }
        return target;
    };

    const saveAndGoTo = async (nextStep: number) => {
        // Mode self : le client est authentifié, pas de sauvegarde du contact
        // (ses données vivent dans le compte) ; navigation locale seulement.
        if (isSelf || !contact) {
            persistStep(nextStep);
            return;
        }
        setSaving(true);
        try {
            const updated = applyFormToContact({ ...contact });
            // Le step serveur reflète la progression (l'étape atteinte - 1, capé sous le récap).
            updated.step = Math.min(nextStep - 1, AiciStep.Recap);
            await TeachrContactRepository.updateTeachrContact(updated);
            setContact(updated);
            persistStep(nextStep);
        } catch (error) {
            console.warn('AICI: échec de sauvegarde du contact', error);
            dispatch({ type: SHOW_ERROR, payload: "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer." });
        } finally {
            setSaving(false);
            if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // ── Soumission finale ───────────────────────────────────────────────────
    const submit = async () => {
        if ((!isSelf && !contact) || submitting) return;
        // Re-valider toutes les étapes (cas reprise depuis un autre navigateur : données
        // restaurées mais jamais validées) et renvoyer sur la première étape invalide.
        const okPersonal = validatePersonal();
        const okBirthBank = validateBirthBank();
        const okAddress = validateAddress();
        const okConditions = validateConditions();
        if (!okPersonal) return goToStep(AiciStep.PersonalInfo);
        if (!okBirthBank) return goToStep(AiciStep.BirthBank);
        if (!okAddress) return goToStep(AiciStep.Address);
        if (!okConditions) return;
        setSubmitting(true);
        dispatch({ type: 'SHOW_DASHBOARD_AICI_LOADER' });
        try {
            if (isSelf) {
                await runAiciSelfSubmission({
                    client: selfClient,
                    // seule une clé validée (contact retrouvé) est transmise
                    contactKey: contact?.key,
                    invitationCode: invitationCode.trim() || undefined,
                    personal,
                    birthBank,
                    address,
                    conditions,
                    isBornFrance,
                });
                // le compte porte maintenant idClientTiersPrestation : on
                // rafraîchit le client en Redux pour que l'espace le reflète
                try {
                    const refreshed = await ClientRepository.getCurrentClient();
                    if (refreshed) {
                        dispatch(userClientSetAccountAction(JSON.parse(JSON.stringify(refreshed))));
                    }
                } catch { /* non bloquant */ }
                persistStep(AiciStep.Confirmation);
                return;
            }
            const target = applyFormToContact({ ...contact });
            await runAiciSubmission({
                teachrContact: target,
                teachr,
                personal,
                birthBank,
                address,
                conditions,
                isBornFrance,
            });
            setContact(target);
            persistStep(AiciStep.Confirmation);
        } catch (error: any) {
            let message: string;
            if (error instanceof AiciSubmissionError && error.details.length > 0) {
                // Erreurs de validation : on liste chaque message renvoyé par l'API
                // (une puce par ligne, le retour à la ligne est rendu par MessageDialog).
                message = error.details.map((detail) => `• ${detail}`).join('\n');
            } else if (error instanceof AiciSubmissionError) {
                message = error.message;
            } else {
                message = "Une erreur est survenue lors de la création de votre compte. Veuillez réessayer.";
            }
            dispatch({ type: SHOW_ERROR, payload: message });
        } finally {
            setSubmitting(false);
            dispatch({ type: 'HIDE_LOADER' });
        }
    };

    return useMemo<AiciOnboardingContextValue>(() => ({
        teachr,
        teachrFirstName: teachr?.firstName,
        loading,
        notFound,
        currentStep,
        serverStep: Number(contact?.step) || 0,
        goToStep,
        saveAndGoTo,
        saving,
        personal,
        personalErrors,
        setPersonal,
        validatePersonal,
        birthBank,
        birthBankErrors,
        setBirthBank,
        validateBirthBank,
        isBornFrance,
        address,
        addressErrors,
        setAddress,
        validateAddress,
        conditions,
        conditionsErrors,
        setConditions,
        validateConditions,
        submit,
        submitting,
        isSelf,
        invitationCode,
        setInvitationCode,
    }), [
        teachr, loading, notFound, currentStep, saving, submitting,
        personal, personalErrors, birthBank, birthBankErrors, isBornFrance,
        address, addressErrors, conditions, conditionsErrors, contact,
        isSelf, invitationCode,
    ]);
};
